import { getCenter } from "ol/extent";
import VectorSource from "ol/source/Vector";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { updateStationAndHighlight } from "../model/actions";

function StationsLayer() {
  const stationsLayer = useSelector((state) => state.stationsLayer);
  const map = useSelector((state) => state.map);
  const config = useSelector((state) => state.config);
  const stations = useSelector((state) => state.stations);
  const visibleStations = useSelector((state) => state.visibleStations);
  const station = useSelector((state) => state.station);
  const auto = useSelector((state) => state.auto);
  const dispatch = useDispatch();
  const backgroundLayer = useSelector((state) => state.backgroundLayer);
  const [selectedUic, setSelectedUic] = useState(null);
  const mode = useSelector((state) => state.mode);

  // Fit on visibleStations
  const fit = useCallback(() => {
    const visibleStationsIds = visibleStations.map((f) => f.get("uic"));
    const stationFeats = stationsLayer
      .getSource()
      .getFeatures()
      .filter((f) => visibleStationsIds.includes(f.get("uic")));

    if (stationFeats.length) {
      const newSource = new VectorSource();
      newSource.addFeatures(stationFeats);
      const visibleStationExtent = newSource.getExtent();

      map.getView().fit(visibleStationExtent, { duration: 0 });
    }
  }, [map, stationsLayer, visibleStations]);

  const fitRef = useRef(fit);
  useEffect(() => {
    fitRef.current = fit;
  }, [fit]);

  // Set style
  useEffect(() => {
    stationsLayer.setStyle((feature, resolution) => {
      if (feature.get("uic") === selectedUic) {
        if (config.selectedStationStyleFunc) {
          return config.selectedStationStyleFunc(feature, resolution);
        }
      }
      return config.stationStyleFunc(feature, resolution);
    });
  }, [config, selectedUic, stationsLayer]);

  // Fill the source
  useEffect(() => {
    if (stations) {
      stationsLayer?.getSource()?.addFeatures(stations);

      // Zoom on visible stations on the previous mode.
      fitRef.current();
    }

    return () => {
      stationsLayer?.getSource()?.clear();
    };
  }, [stations, stationsLayer]);

  // Select station
  useEffect(() => {
    const uic = station ? station.get("uic") : null;

    // We don't want to break the behavior when auto zoom is enabled,
    // see updateAutoMode function in LiveMap.
    if (!auto && uic) {
      map.getView().setCenter(getCenter(station.getGeometry().getExtent()));
    }

    setSelectedUic(uic);
  }, [auto, map, station, stationsLayer]);

  // Update station highlight when mode changes
  useEffect(() => {
    if (mode && selectedUic) {
      dispatch(updateStationAndHighlight(selectedUic, mode));
    }
  }, [mode, dispatch, selectedUic, stationsLayer, map]);

  // Add layer to map and remove on unmount
  useEffect(() => {
    if (stationsLayer) {
      map?.addLayer(stationsLayer);
    }
    return () => {
      map?.removeLayer(stationsLayer);
    };
  }, [map, stationsLayer]);

  return null;
}

export default memo(StationsLayer);
